// Resizable bottom overlay panel with collapse/expand toggle and drag-to-resize
import * as React from "react";
import { CPText } from "shared/components/base";
import "./CPResizableBottomDrawerStyles.scss";

const COLLAPSED_HEIGHT = 32;
const DEFAULT_EXPANDED_HEIGHT = 300;
const MIN_EXPANDED_HEIGHT = 80;
const DRAG_THRESHOLD = 4;

export interface CPResizableBottomDrawerProps {
  title: string;
  children?: React.ReactNode;
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  defaultExpandedHeight?: number;
}

const CPResizableBottomDrawer: React.FC<CPResizableBottomDrawerProps> = ({
  title,
  children,
  isExpanded,
  onExpandedChange,
  defaultExpandedHeight = DEFAULT_EXPANDED_HEIGHT,
}) => {
  const [expandedHeight, setExpandedHeight] = React.useState(
    defaultExpandedHeight,
  );
  const [isDragging, setIsDragging] = React.useState(false);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const dragRef = React.useRef<{
    startY: number;
    startHeight: number;
    hasMoved: boolean;
  } | null>(null);

  const handleBarMouseDown = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      dragRef.current = {
        startY: e.clientY,
        startHeight: expandedHeight,
        hasMoved: false,
      };
    },
    [expandedHeight],
  );

  React.useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const delta = dragRef.current.startY - e.clientY;
      if (!dragRef.current.hasMoved && Math.abs(delta) <= DRAG_THRESHOLD)
        return;
      dragRef.current.hasMoved = true;
      setIsDragging(true);
      const parent = containerRef.current?.parentElement;
      const parentHeight = parent
        ? parent.getBoundingClientRect().height
        : window.innerHeight;
      const newHeight = Math.max(
        MIN_EXPANDED_HEIGHT,
        Math.min(parentHeight * 0.92, dragRef.current.startHeight + delta),
      );
      setExpandedHeight(newHeight);
      if (!isExpanded) onExpandedChange(true);
    };

    const onMouseUp = () => {
      if (dragRef.current) {
        if (!dragRef.current.hasMoved) {
          onExpandedChange(!isExpanded);
        }
        dragRef.current = null;
        setIsDragging(false);
      }
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [isExpanded, onExpandedChange]);

  const currentHeight = isExpanded ? expandedHeight : COLLAPSED_HEIGHT;

  return (
    <div
      ref={containerRef}
      className={`cp-rbd${isDragging ? " cp-rbd-dragging" : ""}`}
      style={{ height: currentHeight }}
    >
      <div className="cp-rbd-bar" onMouseDown={handleBarMouseDown}>
        <CPText style={{ fontSize: "1.3rem", fontWeight: 500 }}>{title}</CPText>
        <span className="cp-rbd-toggle-btn">
          <i className={`fa-solid fa-chevron-${isExpanded ? "down" : "up"}`} />
        </span>
      </div>
      <div className="cp-rbd-content">{children}</div>
    </div>
  );
};

export default CPResizableBottomDrawer;
